import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import { selectWorksheet } from 'services/worksheet/selectors';
import get from 'lodash.get';
import { format } from 'date-fns';

import { AppState } from 'services/types';
import { WorksheetPageIndex } from 'services/worksheet/types';
import { getWorksheetHeaderEntities } from 'services/worksheet/utils';
import { OwnProps } from './index';
import {
    BANDED,
    BANDED_DESCRIPTION,
    BROADCAST,
    HAND_SPRAY,
    HAND_SPRAY_DESCRIPTION,
    HAND_DRESS,
    HAND_DRESS_DESCRIPTION,
    BROADCAST_DESCRIPTION
} from '../../../FertilizerEventFinalForm/components/FertilizerEventForm/components/FertilizerEventApplicationPage/components/FertilizerEventApplicationMethodDropdown/constants';

const selectPageIndex = (
    state: AppState,
    props: OwnProps
): WorksheetPageIndex => props.pageIndex;

const selectFertilizerWorksheetDate = createDeepEqualSelector(
    selectWorksheet,
    (fertilizerWorksheetState) => get(fertilizerWorksheetState, 'date', '')
);

const selectFormattedFertilizerWorksheetDate = createDeepEqualSelector(
    selectFertilizerWorksheetDate,
    (fertilizerWorksheetDateState) => {
        if (fertilizerWorksheetDateState) {
            return format(
                new Date(fertilizerWorksheetDateState),
                'EEE, MMM do, yyyy'
            );
        }

        return '';
    }
);

const makeSelectFormattedFertilizerWorksheetDate =
    (): typeof selectFormattedFertilizerWorksheetDate =>
        selectFormattedFertilizerWorksheetDate;

const selectFertilizerWorksheetApplicationMethod = createDeepEqualSelector(
    selectWorksheet,
    selectPageIndex,
    (fertilizerWorksheetState, pageIndex) => {
        const applicationMethod = get(fertilizerWorksheetState, [
            'pages',
            pageIndex,
            'applicationMethod'
        ]);

        switch (applicationMethod) {
            case BANDED:
                return BANDED_DESCRIPTION;
            case BROADCAST:
                return BROADCAST_DESCRIPTION;
            case HAND_SPRAY:
                return HAND_SPRAY_DESCRIPTION;
            case HAND_DRESS:
                return HAND_DRESS_DESCRIPTION;
            default:
                return '';
        }
    }
);

const makeSelectFertilizerWorksheetApplicationMethod =
    (): typeof selectFertilizerWorksheetApplicationMethod =>
        selectFertilizerWorksheetApplicationMethod;

const selectFertilizerWorksheetUser = createDeepEqualSelector(
    selectWorksheet,
    (fertilizerWorksheetState) => get(fertilizerWorksheetState, 'user')
);

const selectFertilizerWorksheetUserEmail = createDeepEqualSelector(
    selectFertilizerWorksheetUser,
    (fertilizerWorksheetUserState) =>
        get(fertilizerWorksheetUserState, 'email', '')
);

const makeSelectFertilizerWorksheetUserEmail =
    (): typeof selectFertilizerWorksheetUserEmail =>
        selectFertilizerWorksheetUserEmail;

const selectFertilizerWorksheetOperator = createDeepEqualSelector(
    selectWorksheet,
    (fertilizerWorksheetState) => get(fertilizerWorksheetState, 'user')
);

const selectFertilizerWorksheetOperatorFullName = createDeepEqualSelector(
    selectFertilizerWorksheetOperator,
    (worksheetOperator) =>
        worksheetOperator &&
        [worksheetOperator.firstName, worksheetOperator.lastName].join(' ')
);

const makeSelectFertilizerWorksheetOperatorFullName =
    (): typeof selectFertilizerWorksheetOperatorFullName =>
        selectFertilizerWorksheetOperatorFullName;

const selectFertilizerWorksheetEntities = createDeepEqualSelector(
    selectWorksheet,
    selectPageIndex,
    (fertilizerWorksheetState, pageIndex) => {
        const entitySummaries = get(fertilizerWorksheetState, [
            'pages',
            pageIndex,
            'entitySummaries'
        ]);

        return getWorksheetHeaderEntities(entitySummaries);
    }
);

const makeSelectFertilizerWorksheetEntities =
    (): typeof selectFertilizerWorksheetEntities =>
        selectFertilizerWorksheetEntities;

export {
    selectFertilizerWorksheetDate,
    selectFertilizerWorksheetUser,
    selectFertilizerWorksheetUserEmail,
    makeSelectFertilizerWorksheetUserEmail,
    makeSelectFormattedFertilizerWorksheetDate,
    makeSelectFertilizerWorksheetEntities,
    makeSelectFertilizerWorksheetOperatorFullName,
    makeSelectFertilizerWorksheetApplicationMethod
};
